import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import { useLocation } from "react-router-dom";
import { toast } from "@/utils/toast";

interface UsersFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const createUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  rol: z.enum(["admin", "client"]),
});

const editUserSchema = createUserSchema.omit({ password: true });

type CreateUserFormValues = z.infer<typeof createUserSchema>;
type EditUserFormValues = z.infer<typeof editUserSchema>;

export default function Profile({ onSuccess, onCancel }: UsersFormProps) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const userId = location.state?.userId;

  // Use different form types based on whether we're editing or creating
  const form = useForm<CreateUserFormValues | EditUserFormValues>({
    resolver: zodResolver(userId ? editUserSchema : createUserSchema),
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      rol: "client",
      ...(userId ? {} : { password: "" }),
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await fetch(getApiUrl(`users/${userId}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch user");
      }
      return response.json();
    },
    enabled: !!userId,
  });

  useEffect(() => {
    if (data) {
      form.reset({
        email: data.email,
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        rol: data.rol || "client",
      });
    }
  }, [data, form]);

  const mutation = useMutation({
    mutationFn: async (values: CreateUserFormValues | EditUserFormValues) => {
      const url = userId ? getApiUrl(`users/${userId}/`) : getApiUrl("users/");
      const method = userId ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save user");
      }
      return response.json();
    },
    onSuccess: () => {
      console.log("Mutation onSuccess");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(`User ${userId ? "updated" : "created"} successfully`);
      onSuccess?.();
    },
    onError: (err: Error) => {
      console.log("Mutation onError", err);
      toast.error(`Error: ${err.message}`);
    },
  });

  const onSubmit = (values: CreateUserFormValues | EditUserFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-[calc(100vh-200px)]"
      >
        <div className="space-y-6 overflow-y-auto flex-1 px-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Email"
                    {...field}
                    readOnly={!!userId}
                    className={userId ? "bg-muted" : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!userId && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rol"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-auto">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" type="submit" loading={mutation.isLoading}>
            {userId ? "Save Changes" : "Create User"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
